import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { addDays, berlinDate, coveredDays, isMonth, isoWeek, isRealDate, monthGrid, monthOf, shiftMonth, timeLabel, weekday, weekStart, type Appointment, type CalendarTask } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { useSession } from '../../session';
import { useAssignees } from './assignees';
import { toneClass, useAppointmentTypes } from './appointmentTypes';
import { CalendarTabs } from './CalendarTabs';
import { itemHref, taskMark, taskToItem, type CalItem } from './calendarItems';
import { PersonDot } from './PersonDot';
import { ShareCalendarPanel, useCalendarShares } from './ShareCalendarPanel';
import { TimeGrid } from './TimeGrid';
import './calendar.css';

type View = 'day' | 'week' | 'month';
const VIEWS: { key: View; label: string }[] = [
  { key: 'day', label: 'Tag' },
  { key: 'week', label: 'Woche' },
  { key: 'month', label: 'Monat' },
];

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const monthTitle = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });
const dayTitle = new Intl.DateTimeFormat('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const dayShort = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'long' });
const dayMonth = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' });
const at = (day: string) => new Date(`${day}T12:00:00`);

/** Wie viele Termine eine Zelle des Rasters beschriftet. Mehr zeigt „+ n weitere“, auf dem Handy nur Punkte. */
const CHIPS_PER_DAY = 3;

/** Darf der Browser schmal sein? Auf dem Handy zeigt die Woche die Tage untereinander statt nebeneinander. */
function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia('(max-width: 719px)').matches);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 719px)');
    const listener = () => setNarrow(query.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);
  return narrow;
}

/**
 * Kalender der Termine in drei Ansichten: Tag und Woche als Stundenraster, Monat als Raster mit markierten Tagen (KON-070). Die Ansicht, der Tag, die Person, die Art und die Suche
 * stehen in der Adresszeile, damit ein Link genau diese Ansicht öffnet. Was „heute“ ist, gilt in Europe/Berlin. Auf dem Handy zeigt die Woche die Tage untereinander.
 */
export function AppointmentsPage() {
  const [params, setParams] = useSearchParams();
  const { can, session } = useSession();
  const assignees = useAssignees();
  const types = useAppointmentTypes();
  const shares = useCalendarShares();
  const narrow = useNarrow();
  const today = berlinDate();
  const [sharing, setSharing] = useState(false);

  const viewParam = params.get('view');
  const view: View = viewParam === 'day' || viewParam === 'week' ? viewParam : 'month';
  const monthParam = params.get('month') ?? '';
  const dayParam = params.get('day') ?? '';
  const q = params.get('q') ?? '';
  const typeId = params.get('type') ?? '';
  // Aufgaben erscheinen nur als Termin im Kalender, wenn sie übernommen wurden. Wer auch die übrigen Aufgaben mit Zeitraum sehen will, schaltet sie im Filter ein.
  const showTasks = params.get('tasks') === '1';
  // „mine=1“ stammt aus älteren Links und bedeutet dasselbe wie die eigene Person
  const personParam = params.get('person') ?? (params.get('mine') === '1' ? (session?.user.id ?? '') : '');

  // Der Bezugstag der Ansicht. Ohne Angabe heute, bei einem anderen gewählten Monat dessen erster Tag.
  const month = isMonth(monthParam) ? monthParam : isRealDate(dayParam) ? monthOf(dayParam) : monthOf(today);
  const anchor = isRealDate(dayParam) ? dayParam : monthOf(today) === month ? today : `${month}-01`;
  const grid = useMemo(() => monthGrid(month), [month]);
  // Im Monat ist ohne Auswahl der heutige Tag (oder der Erste) gewählt, in Tag und Woche der Bezugstag
  const selected = view === 'month' ? (grid.days.includes(anchor) && monthOf(anchor) === month ? anchor : monthOf(today) === month ? today : `${month}-01`) : anchor;

  const range = useMemo(() => {
    if (view === 'month') return { from: grid.from, to: grid.to, days: grid.days };
    if (view === 'day') return { from: anchor, to: anchor, days: [anchor] };
    const first = weekStart(anchor);
    const days = Array.from({ length: 7 }, (_, i) => addDays(first, i));
    return { from: first, to: days[6]!, days };
  }, [view, grid, anchor]);

  const list = useQuery({
    queryKey: ['appointments', { from: range.from, to: range.to, involvedUserIds: personParam, typeIds: typeId, q }],
    queryFn: async () => (await api<{ data: Appointment[] }>('GET', `/appointments${queryString({ from: range.from, to: range.to, involvedUserIds: personParam, typeIds: typeId, q })}`)).data,
    placeholderData: keepPreviousData,
  });

  // Aufgaben mit Zeitraum, die noch kein Termin sind, auf Wunsch wie ganztägige Termine (KON-087)
  const taskList = useQuery({
    enabled: showTasks,
    queryKey: ['calendar-tasks', { from: range.from, to: range.to, involvedUserIds: personParam, typeIds: typeId, q }],
    queryFn: async () => (await api<{ data: CalendarTask[] }>('GET', `/tasks/calendar${queryString({ from: range.from, to: range.to, involvedUserIds: personParam, typeIds: typeId, q })}`)).data,
    placeholderData: keepPreviousData,
  });
  const items = useMemo<CalItem[]>(() => [...(list.data ?? []), ...(showTasks ? (taskList.data ?? []).map(taskToItem) : [])], [list.data, taskList.data, showTasks]);

  // Einträge je Tag. Ein Eintrag über mehrere Tage steht an jedem seiner Tage, ganztägige zuerst.
  const byDay = useMemo(() => {
    const map = new Map<string, CalItem[]>();
    for (const a of items) {
      for (const day of coveredDays(a)) {
        if (day < range.from || day > range.to) continue;
        map.set(day, [...(map.get(day) ?? []), a]);
      }
    }
    for (const items of map.values()) items.sort((x, y) => Number(y.allDay) - Number(x.allDay) || x.start.localeCompare(y.start));
    return map;
  }, [items, range.from, range.to]);

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    // Aus älteren Links
    if ('person' in next) merged.delete('mine');
    setParams(merged);
  };
  const setView = (next: View) => update({ view: next === 'month' ? '' : next, month: '', day: anchor === today ? '' : selected });
  const goToday = () => update({ month: '', day: '' });
  /** Vor und zurück: einen Tag, eine Woche oder einen Monat */
  const step = (n: -1 | 1) => {
    if (view === 'month') {
      const target = shiftMonth(month, n);
      update({ month: target === monthOf(today) ? '' : target, day: '' });
    } else {
      const target = addDays(anchor, n * (view === 'week' ? 7 : 1));
      update({ day: target === today ? '' : target, month: '' });
    }
  };

  const me = session?.user.id ?? '';
  const canWrite = can('calendar.write');
  // Wer alle sehen darf (Büro, Inhaber, Rollen mit „Termine aller ansehen“), wählt jeden Kollegen im Filter
  const seesAll = canWrite || can('calendar.read_all');
  const nameOf = (id: string) => assignees.name(id);
  // Personen im Filter: Wer alle sieht (Büro, Inhaber, „Termine aller ansehen“), sucht unter allen Kollegen, bei vielen Mitarbeitern per Suchfeld mit Vorschlägen.
  // Alle anderen wählen sich selbst und die Kollegen, die ihren Kalender für sie freigegeben haben. Mehrere Personen gelten zugleich.
  const people = seesAll
    ? assignees.list.map((a) => ({ value: a.id, label: a.id === me ? `${a.name} (ich)` : a.name }))
    : [{ value: me, label: `${session?.user.name ?? 'Ich'} (ich)` }, ...(shares.data?.sharedWithMe ?? []).map((s) => ({ value: s.userId, label: s.name }))];
  const fields: FilterField[] = [
    { key: 'person', label: 'Personen', type: 'people', placeholder: 'Name suchen …', options: people },
    { key: 'tasks', label: 'Aufgaben', type: 'select', options: [{ value: '', label: 'Nur übernommene (als Termin)' }, { value: '1', label: 'Auch Aufgaben ohne Termin' }] },
    ...(types.list.length > 0 ? [{ key: 'type', label: 'Art', type: 'multi' as const, options: types.list.map((t) => ({ value: t.id, label: t.name })) }] : []),
  ];

  const title =
    view === 'month'
      ? monthTitle.format(at(`${month}-01`))
      : view === 'day'
        ? dayTitle.format(at(anchor))
        : (() => {
            const { week } = isoWeek(anchor);
            return `KW ${week} · ${dayMonth.format(at(range.from))} – ${dayMonth.format(at(range.to))} ${range.to.slice(0, 4)}`;
          })();
  const unit = view === 'month' ? 'Monat' : view === 'week' ? 'Woche' : 'Tag';

  const dayItems = byDay.get(selected) ?? [];
  const names = (ids: string[]) => ids.map((x) => nameOf(x) ?? 'Nicht mehr verfügbar').join(', ');
  const newDay = view === 'month' ? selected : anchor;

  /** Eine Zeile der Terminliste: freigegebene Termine ohne Verweis und ohne Details */
  const row = (a: CalItem, day: string) => (
    <li key={a.id}>
      <span className="cal__time">{a.kind === 'task' ? (a.done ? 'Aufgabe, erledigt' : 'Aufgabe') : timeLabel(a, day)}</span>
      <span className="cal__what">
        {a.masked ? (
          <>
            <span className="strong-link is-masked"><PersonDot item={a} nameOf={nameOf} />{a.title}</span>
            <span className="muted small">Freigegeben von {names(a.sharedByUserIds) || 'einem Kollegen'}, Details nicht einsehbar</span>
          </>
        ) : (
          <>
            <Link to={itemHref(a)} className={`strong-link${a.done ? ' is-done' : ''}`}><PersonDot item={a} nameOf={nameOf} />{a.title}</Link>
            {types.find(a.typeId) && <span className={`type-badge ${toneClass(types.find(a.typeId)!.color)}`}>{types.find(a.typeId)!.name}</span>}
            {a.kind === 'task' ? (
              <span className="muted small">
                {[`Zuständig: ${a.assignedUserId ? (nameOf(a.assignedUserId) ?? 'Nicht mehr verfügbar') : 'niemand'}`, a.participantUserIds.length > 0 ? `Beteiligt: ${names(a.participantUserIds)}` : ''].filter(Boolean).join(' · ')}
              </span>
            ) : (
              (a.location || a.participantUserIds.length > 0) && (
                <span className="muted small">{[a.location, a.participantUserIds.length > 0 ? `Mit ${names(a.participantUserIds)}` : ''].filter(Boolean).join(' · ')}</span>
              )
            )}
          </>
        )}
      </span>
    </li>
  );

  return (
    <>
      <CalendarTabs />
      <div className="page-head">
        <h1>Termine</h1>
        <span className="muted">{list.data && taskList.data ? `${items.length} im sichtbaren Zeitraum` : ''}</span>
        <button type="button" className="btn btn--secondary btn--sm page-head__action cal__share" onClick={() => setSharing((s) => !s)} aria-expanded={sharing}>Kalender freigeben</button>
        <Link className="btn btn--primary btn--sm" to={`/kalender/termine/neu?day=${newDay}`}>Neuer Termin</Link>
      </div>

      {sharing && <ShareCalendarPanel onClose={() => setSharing(false)} />}

      <FilterBar
        search={{ value: q, placeholder: 'Titel oder Ort suchen', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ person: personParam, type: typeId, tasks: showTasks ? '1' : '' }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams([...params.entries()].filter(([k]) => k === 'month' || k === 'day' || k === 'view')))}
      />

      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}

      <div className="cal__head">
        <button type="button" className="btn btn--secondary cal__nav" onClick={() => step(-1)} aria-label={unit === 'Woche' ? 'Vorherige Woche' : `Vorheriger ${unit}`}>‹</button>
        <h2 className="cal__title" aria-live="polite">{title}</h2>
        <button type="button" className="btn btn--secondary cal__nav" onClick={() => step(1)} aria-label={unit === 'Woche' ? 'Nächste Woche' : `Nächster ${unit}`}>›</button>
        <button type="button" className="btn btn--secondary btn--sm cal__today" onClick={goToday}>Heute</button>
        <div className="seg cal__views" role="group" aria-label="Ansicht">
          {VIEWS.map((v) => (
            <button key={v.key} type="button" className={`seg__item${view === v.key ? ' is-active' : ''}`} aria-pressed={view === v.key} onClick={() => setView(v.key)}>{v.label}</button>
          ))}
        </div>
      </div>

      {view === 'month' && (
        <>
          <div className="cal__grid" role="grid" aria-label={monthTitle.format(at(`${month}-01`))}>
            {WEEKDAYS.map((d) => (
              <div key={d} className="cal__dow" role="columnheader">{d}</div>
            ))}
            {grid.days.map((day) => {
              const items = byDay.get(day) ?? [];
              const classes = ['cal__day'];
              if (monthOf(day) !== month) classes.push('cal__day--outside');
              if (items.length > 0) classes.push('cal__day--active');
              if (day === today) classes.push('cal__day--today');
              if (day === selected) classes.push('cal__day--selected');
              if (weekday(day) >= 5) classes.push('cal__day--weekend');
              const tone = (a: CalItem) => (a.masked ? 'is-masked' : toneClass(types.find(a.typeId)?.color)) + (a.kind === 'task' ? ' cal__event--task' : '') + (a.done ? ' is-done' : '');
              return (
                <button
                  key={day}
                  type="button"
                  role="gridcell"
                  className={classes.join(' ')}
                  aria-pressed={day === selected}
                  aria-current={day === today ? 'date' : undefined}
                  aria-label={`${dayTitle.format(at(day))}, ${items.length === 0 ? 'keine Termine' : items.length === 1 ? '1 Termin' : `${items.length} Termine`}`}
                  onClick={() => update({ day: day === today && monthOf(today) === month ? '' : day, month: month === monthOf(today) ? '' : month })}
                >
                  <span className="cal__num">{Number(day.slice(8))}</span>
                  {items.slice(0, CHIPS_PER_DAY).map((a) => (
                    <span key={a.id} className={`cal__event ${tone(a)}`}>
                      <PersonDot item={a} nameOf={nameOf} />
                      {taskMark(a)}
                      {!a.allDay && a.start.slice(0, 10) === day ? `${a.start.slice(11)} ` : ''}
                      {a.title}
                    </span>
                  ))}
                  {items.length > CHIPS_PER_DAY && <span className="cal__more">+ {items.length - CHIPS_PER_DAY} weitere</span>}
                  {items.length > 0 && (
                    <span className="cal__dots" aria-hidden="true">
                      {items.slice(0, 3).map((a) => <i key={a.id} className={`cal__dot ${tone(a)}`} />)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <section className="card cal__day-panel" aria-live="polite">
            <div className="page-head">
              <h2>{dayTitle.format(at(selected))}</h2>
              <Link className="btn btn--secondary btn--sm page-head__action" to={`/kalender/termine/neu?day=${selected}`}>Termin hinzufügen</Link>
            </div>
            {list.isLoading && <p className="muted">Wird geladen …</p>}
            {!list.isLoading && dayItems.length === 0 && <p className="muted">Keine Termine am {dayShort.format(at(selected))}.</p>}
            <ul className="cal__list">{dayItems.map((a) => row(a, selected))}</ul>
          </section>
        </>
      )}

      {view === 'day' && (
        <>
          {list.isLoading && <p className="muted">Wird geladen …</p>}
          <TimeGrid days={[anchor]} appointments={items} types={types} today={today} nameOf={nameOf} />
        </>
      )}

      {view === 'week' &&
        (narrow ? (
          <div className="cal__weeklist">
            {list.isLoading && <p className="muted">Wird geladen …</p>}
            {range.days.map((day) => {
              const items = byDay.get(day) ?? [];
              return (
                <section key={day} className={`card cal__weekday${day === today ? ' is-today' : ''}`} aria-label={dayTitle.format(at(day))}>
                  <button type="button" className="cal__weekday-head" onClick={() => update({ view: 'day', day: day === today ? '' : day })}>
                    <span>{dayTitle.format(at(day))}</span>
                    <span className="muted small">{items.length === 0 ? 'Keine Termine' : items.length === 1 ? '1 Termin' : `${items.length} Termine`}</span>
                  </button>
                  {items.length > 0 && <ul className="cal__list">{items.map((a) => row(a, day))}</ul>}
                </section>
              );
            })}
          </div>
        ) : (
          <>
            {list.isLoading && <p className="muted">Wird geladen …</p>}
            <TimeGrid days={range.days} appointments={items} types={types} today={today} nameOf={nameOf} onPickDay={(day) => update({ view: 'day', day: day === today ? '' : day })} />
          </>
        ))}
    </>
  );
}
